import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { UserRole } from '../../types/adminUser';
import {
  DEFAULT_PREFERENCES,
  MeResponse,
  UserPreferencesPayload,
  UserProfile,
  normalizePreferences,
} from '../../types/user';

/**
 * The signed-in user: profile, roles and preferences (#378).
 *
 * Preferences start at the defaults rather than null, so every consumer can read
 * them without a guard and the screen before the response looks like the screen
 * after it.
 */
interface UserState {
  profile: UserProfile | null;
  roles: UserRole[];
  preferences: UserPreferencesPayload;
  /** True once GET /me has answered, however it answered. */
  loaded: boolean;
  /** Set when preferences could not be loaded or saved. */
  error: string | null;
  saving: boolean;
}

const initialState: UserState = {
  profile: null,
  roles: [],
  preferences: DEFAULT_PREFERENCES,
  loaded: false,
  error: null,
  saving: false,
};

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    /** GET /me answered. */
    meLoaded: (state, action: PayloadAction<MeResponse>) => {
      state.profile = action.payload.user ?? null;
      state.roles = action.payload.roles ?? [];
      state.preferences = normalizePreferences(
        action.payload.preferences?.payload
      );
      state.loaded = true;
      state.error = null;
    },

    /**
     * GET /me failed. The defaults stay in place and `loaded` still flips: the
     * app is usable without preferences, and blocking on them would make a
     * settings outage look like a broken dashboard.
     */
    meFailed: (state, action: PayloadAction<string>) => {
      state.loaded = true;
      state.error = action.payload;
    },

    preferencesSaving: (state) => {
      state.saving = true;
      state.error = null;
    },

    /** PUT succeeded — adopt exactly what the server stored. */
    preferencesSaved: (state, action: PayloadAction<UserPreferencesPayload>) => {
      state.preferences = normalizePreferences(action.payload);
      state.saving = false;
      state.error = null;
    },

    preferencesSaveFailed: (state, action: PayloadAction<string>) => {
      state.saving = false;
      state.error = action.payload;
    },

    /** Local edit before saving, so the form reflects the user's typing. */
    preferencesChanged: (
      state,
      action: PayloadAction<Partial<UserPreferencesPayload>>
    ) => {
      state.preferences = normalizePreferences({
        ...state.preferences,
        ...action.payload,
      });
    },
  },
});

export const {
  meLoaded,
  meFailed,
  preferencesSaving,
  preferencesSaved,
  preferencesSaveFailed,
  preferencesChanged,
} = userSlice.actions;

export default userSlice.reducer;
